import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { 
  Search, 
  Filter,
  X, 
  Copy, 
  Eye, 
  Brain,
  CheckCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import PromptModal from './PromptModal';
import LoadingSpinner from './LoadingSpinner';
import Navigation from './Navigation';

/**
 * PromptLibrary Component
 * 
 * Displays a searchable and filterable grid of AI prompts with modal preview functionality.
 * Supports both single prompts and multi-part prompt modules with consistent UI.
 * 
 * Features:
 * - Search functionality across prompt titles and descriptions
 * - Category filtering with dropdown (consistent with Videos/Resources pages)
 * - Modal preview with copy-to-clipboard functionality
 * - Multi-part prompt support with unified display
 * - Responsive grid layout (1 column mobile, 2 tablet, 3 desktop)
 * - Category icons and color-coded badges
 * - Clear filters functionality
 * 
 * Multi-part Prompt Display:
 * - Shows as single card with "View" and "Copy" buttons (same as single prompts)
 * - Displays "X Parts" badge to indicate multi-part nature
 * - "View" button opens modal showing all parts in order
 * - Each part has individual copy functionality
 * - "Copy All Parts" combines all parts with separators
 * 
 * API Endpoints Used:
 * - GET /api/prompts - Fetches all active prompts for users (includes multi-part grouping)
 * - GET /api/admin/categories - Fetches categories for filtering (admin endpoint)
 * 
 * @returns {JSX.Element} AI Prompt Library interface
 */

const PromptLibrary = () => {
  const { user } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');

  // Load prompts and categories on component mount
  useEffect(() => {
    loadPrompts();
    loadCategories();
  }, []);

  // Filter prompts when search query or category changes
  useEffect(() => {
    filterPrompts();
  }, [prompts, searchQuery, selectedCategory]);

  /**
   * Loads prompts from the public API endpoint
   * Uses /api/prompts which returns all active prompts for authenticated users
   * Includes cache-busting parameter to ensure fresh data
   */
  const loadPrompts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/prompts?t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Prompts API response:', data);
        // The backend now returns grouped multi-part prompts, no need to filter by is_active
        const allPrompts = Array.isArray(data.data) ? data.data : [];
        setPrompts(allPrompts);
      } else {
        setError('Failed to load prompts');
      }
    } catch (error) {
      setError('Error loading prompts');
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Loads categories from the admin API endpoint
   * Uses /api/admin/categories which returns all categories for filtering
   * Note: Uses admin endpoint because it has the complete category list
   * Response structure: { data: { categories: [...] } }
   */
  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/categories?t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Categories API response:', data);
        setCategories(data.data?.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const filterPrompts = () => {
    let filtered = prompts;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(prompt => 
        prompt.title.toLowerCase().includes(query) ||
        prompt.description.toLowerCase().includes(query)
      );
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(prompt => prompt.category_id === selectedCategory);
    }

    setFilteredPrompts(filtered);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleCategoryFilter = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
  };

  const handlePreviewPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setShowModal(true);
  };

  const handleCopyPrompt = async (promptText) => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopySuccess('Prompt copied to clipboard!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (error) {
      console.error('Failed to copy prompt:', error);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'Marketing & Sales': '📊',
      'Financial Analysis': '💰',
      'Team Management': '👥',
      'Operations': '⚙️',
      'Product Development': '🚀',
      'Customer Research': '🔍'
    };
    return iconMap[categoryName] || '📝';
  };

  const selectedCategoryName = Array.isArray(categories) ? categories.find(cat => cat.id === selectedCategory)?.name : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Prompts</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadPrompts} className="bg-blue-600 hover:bg-blue-700">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center space-x-3 mb-2">
            <Brain className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">AI Prompt Library</h1>
          </div>
          <p className="text-gray-600 text-lg">
            Business prompts for ChatGPT, Claude & other AI tools
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Search and Filter */}
        <div className="mb-8 space-y-4 md:space-y-0 md:flex md:items-center md:space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search prompts..."
              value={searchQuery}
              onChange={handleSearch}
              className="pl-12 h-14 border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-2xl bg-white/80 backdrop-blur-sm transition-all duration-200"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-gray-500" />
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryFilter(e.target.value)}
                className="h-14 px-4 pr-10 border border-gray-300 rounded-2xl bg-white/80 backdrop-blur-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 appearance-none cursor-pointer min-w-[200px]"
              >
                <option value="">All Categories</option>
                {Array.isArray(categories) ? categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                )) : null}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

          {/* Results Counter and Clear Filters */}
          <div className="flex items-center justify-between">
            <p className="text-gray-600">
              Showing {filteredPrompts.length} of {prompts.length} prompts
            </p>
            <div className="flex items-center space-x-4">
              {(searchQuery || selectedCategory) && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
                >
                  <X className="w-4 h-4" />
                  <span>Clear Filters</span>
                </Button>
              )}
              {copySuccess && (
                <div className="flex items-center space-x-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">{copySuccess}</span>
                </div>
              )}
            </div>
          </div>

        {/* Prompts Grid */}
        {filteredPrompts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No prompts found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || selectedCategory 
                ? 'Try adjusting your search or filter criteria'
                : 'No prompts available yet'
              }
            </p>
            {(searchQuery || selectedCategory) && (
              <Button onClick={clearFilters} variant="outline">
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPrompts.map((prompt) => (
              <Card key={prompt.id} className="p-6 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{getCategoryIcon(prompt.category_name)}</span>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg leading-tight">
                        {prompt.title}
                      </h3>
                      {prompt.is_multi_part && (
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {prompt.total_parts} Parts
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {prompt.description}
                </p>

                <div className="flex items-center justify-between mb-4">
                  <Badge 
                    variant="secondary" 
                    className="text-xs"
                    style={{ backgroundColor: `${prompt.category_color}20`, color: prompt.category_color }}
                  >
                    📂 {prompt.category_name}
                  </Badge>
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreviewPrompt(prompt)}
                    className="flex-1 flex items-center justify-center space-x-2"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleCopyPrompt(prompt.prompt_text)}
                    className="flex-1 flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

      {/* Prompt Modal */}
      {showModal && selectedPrompt && (
        <PromptModal
          prompt={selectedPrompt}
          onClose={() => {
            setShowModal(false);
            setSelectedPrompt(null);
          }}
          onCopy={handleCopyPrompt}
        />
      )}

      </div>
    </div>
  );
};

export default PromptLibrary;
